import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, MapPinIcon, ChevronDownIcon, PhoneIcon, UsersIcon, PhoneOffIcon, CalendarXIcon, TrendingDownIcon, BarChart3Icon, BuildingIcon, LockIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
const faqs = [{
  question: 'What does Viridia Analytics actually do for home service businesses?',
  answer: 'We build predictable growth engines for HVAC, painting, landscaping, and contractor businesses across Phoenix. Our core focus is speed-to-lead: we use AI-assisted automation to respond to inquiries instantly, manage targeted ad campaigns, and build conversion-focused websites to turn more traffic into booked appointments.'
}, {
  question: 'Do you help with lead follow-up after ads start running?',
  answer: 'Yes. That is our primary differentiator. We do not just generate clicks and walk away. We implement AI systems that respond to text messages, lead platform inquiries, and web forms in under 60 seconds, 24/7, ensuring leads do not go cold.'
}, {
  question: 'What is AI lead conversion?',
  answer: 'It is a system where an AI assistant handles initial inbound communication. When a lead reaches out, the AI instantly replies, answers common questions, qualifies the prospect based on your services, and can even book the estimate directly on your calendar.'
}, {
  question: 'Can you work with my current CRM?',
  answer: 'In many cases, yes. We build custom automations to connect your existing tools. If your current CRM is holding you back, we can also migrate you to a centralized command center that handles pipeline visibility, invoicing, and communications in one place.'
}, {
  question: 'Do you only work with Phoenix businesses?',
  answer: 'We are headquartered in Phoenix, Arizona, and specialize in this local market. However, we also partner with home service businesses remotely, including clients in Los Angeles and Long Beach.'
}, {
  question: 'Do you help with Yelp ads?',
  answer: 'Yes. We are a Yelp Advertising Premier Partner. We have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies to maximize your return on ad spend.'
}, {
  question: 'Can you improve how fast we respond to new leads?',
  answer: 'Absolutely. Our systems are designed to eliminate the delay between a lead reaching out and getting a response. We replace slow human follow-up with instant AI responses and missed-call text-back automation.'
}, {
  question: 'Do I need a new website to work with you?',
  answer: 'No. While we do build high-converting, mobile-first websites, they are optional conversion assets. We can often implement our AI lead conversion and ad management systems using your existing site.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1955" data-mp="3rprqoxrwr11z">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" aria-expanded={isOpen} data-id="element-1956" data-mp="j8oj4dp55hns">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1957" data-mp="a8rxkky1sywy">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1958" data-mp="e8sd68wvvg5l" />
      </button>
      <AnimatePresence data-id="element-1959" data-mp="2z1dnm3lypejk">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1960" data-mp="2yxzp56gf5phe">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1961" data-mp="1zw0xfjcolnx">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1962" data-mp="3o7m1rc93z2qt">
      <SEOHead title="AI Marketing Agency Phoenix | Lead Conversion for Home Services | Viridia Analytics" description="Viridia Analytics is a Phoenix AI marketing agency helping home service businesses stop losing leads with AI lead conversion, speed-to-lead automation, and ad management." canonical="/" keywords="marketing agency phoenix, AI marketing agency, AI advertising for contractors, painters, HVAC, landscapers, contractors, window installers, AI lead conversion, local ads management, home services marketing" jsonLd={LOCAL_BUSINESS_SCHEMA} faq={faqs} data-id="element-1963" data-mp="3oa8cfkrapyd9" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-1964" data-mp="1210s0r70kxd8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1965" data-mp="1703woaj311ko">
          <div className="max-w-3xl" data-id="element-1966" data-mp="1726q74jg1cu5">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1967" data-mp="1by5eks4ruuuk">
              Stop Losing Leads You{' '}
              <span className="text-viridia" data-id="element-1968" data-mp="3mst3i73dlajm">Already Paid For</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-xl sm:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-1969" data-mp="tycifhe03jt9">
              Viridia Analytics helps home service businesses respond faster,
              follow up smarter, and turn more inbound leads into booked
              appointments - with AI-assisted automation, ad management, and
              conversion-focused systems.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-1970" data-mp="1hog96o63bp2u">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-1971" data-mp="2f5kizbs3thcd">
                See Where Your Leads Are Leaking
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1972" data-mp="1rfzmdq312lkm" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PARTNER LOGOS */}
      <section className="py-8 bg-viridia-black border-t border-gray-800 overflow-hidden" data-id="element-1973" data-mp="1hl7mugpvxon7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 text-center" data-id="element-1974" data-mp="1mk10qa933hnw">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1975" data-mp="18iye9we6g5cg">
            Trusted by local service businesses
          </p>
        </div>
        <div className="relative flex overflow-x-hidden" data-id="element-1976" data-mp="1cmmn0iejp6za">
          <motion.div className="flex items-center gap-16 px-8 whitespace-nowrap" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: 40,
          ease: 'linear',
          repeat: Infinity
        }} data-id="element-1977" data-mp="1syzycpo1lgu2">
            {/* Quadruple the logos to ensure no gaps on ultrawide screens */}
            {[...Array(4)].map((_, i) => <Fragment key={i} data-id="element-1978" data-mp="1a39sgrczo0aw" data-mpi={i}>
                <img src="https://cdn.magicpatterns.com/uploads/vu3TeukAscWBmXCriEutP3/logo.webp" alt="AZGlo Logo" className="h-20 object-contain rounded-lg" data-id="element-1979" data-mp="15a6v0853c6pf" />
                <img src="https://cdn.magicpatterns.com/uploads/oRd3jcQ42CFjrTA2feQhft/logo.jpg" alt="Gomez Landscaping Logo" className="h-20 object-contain rounded-lg" data-id="element-1980" data-mp="vdvfl9a7f0qr" />
                <img src="https://cdn.magicpatterns.com/uploads/oKhtaguHDZQ1jwnsXbrpm4/logo.png" alt="Wall Candy Logo" className="h-20 object-contain rounded-lg" data-id="element-1981" data-mp="me8i2yklif2z" />
                <img src="https://cdn.magicpatterns.com/uploads/qU8ovRMu8iQdUH4ewRRymJ/logo.jpg" alt="TFP Landscaping Logo" className="h-20 object-contain rounded-lg" data-id="element-1982" data-mp="36zmi4rs4hpy0" />
                <img src="https://cdn.magicpatterns.com/uploads/jzSYXmHzY9XrXrg5hvwEvR/logo.jpg" alt="Nuñez Painting Logo" className="h-20 object-contain rounded-lg" data-id="element-1983" data-mp="2ed1t3xz4dpxm" />
                <img src="https://cdn.magicpatterns.com/uploads/fGFztLtAhzgRsKrhGD2vLx/logo-01.webp" alt="AQ Communications Logo" className="h-20 object-contain rounded-lg" data-id="element-1984" data-mp="2szuyobiu7hcm" />
                <img src="https://cdn.magicpatterns.com/uploads/iv4wx5k6BUCRwoL6bVBSqh/Logo.png" alt="MAJ Countertops Logo" className="h-20 object-contain rounded-lg" data-id="element-1985" data-mp="20iiu7widx4k2" />
                <img src="https://cdn.magicpatterns.com/uploads/bMUopew95CsBGQxBnkcNtY/logo.jpg" alt="Finish Coat Painting Logo" className="h-20 object-contain rounded-lg" data-id="element-1986" data-mp="2grk2t10nyc2u" />
              </Fragment>)}
          </motion.div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="py-12 bg-viridia-surface border-y border-gray-800" data-id="element-1987" data-mp="1y010yxidl42e">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1988" data-mp="22y2phxyklvl9">
          <ScrollReveal data-id="element-1989" data-mp="27p2jovgb1zk2">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" data-id="element-1990" data-mp="27pavek0oyyto">
              <div data-id="element-1991" data-mp="1aa6pokdpfnk9">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1992" data-mp="1a8vomsbn8twu">242</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1993" data-mp="1f888r5b4rmvq">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1994" data-mp="3si8cljz33h4u">TFP Landscaping</p>
              </div>
              <div data-id="element-1995" data-mp="3oi7of7m6nrkl">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1996" data-mp="3oflifcexxk4h">28</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1997" data-mp="3jgs3rgnx3n2q">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1998" data-mp="39jx7z5j140s5">TFP Landscaping</p>
              </div>
              <div data-id="element-1999" data-mp="34tp699zlhefe">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2000" data-mp="34t6yph7hfds6">39.9%</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2001" data-mp="30rlsuftd7zrh">
                  Close Rate
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-2002" data-mp="2vu21ce7npqhl">TFP Landscaping</p>
              </div>
              <div data-id="element-2003" data-mp="2qscpiwcm18sg">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2004" data-mp="2qsw7ue669z70">138</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2005" data-mp="2lvmbeekd8n9o">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2006" data-mp="2c9bl3y3gbq2w">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-2007" data-mp="2372ehjbb0gus">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2008" data-mp="237ltos9kkr54">29</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2009" data-mp="27744gv7oflu8">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2010" data-mp="2uzjkq67xzyec">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-2011" data-mp="2m5oeeuv341x4">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2012" data-mp="2m43ltasbyt1w">$15.05k</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2013" data-mp="2q68arw7u31i7">
                  Closed-Won Revenue
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2014" data-mp="3dv1a82nnzrhk">
                  From AI-worked leads only
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 bg-viridia-black" data-id="element-2015" data-mp="1298z91rrqom5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2016" data-mp="128pcn7r6bc1s">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-2017" data-mp="1gy54kr2fxnj2">
            <div className="lg:col-span-5" data-id="element-2018" data-mp="1by8x2hujbdyr">
              <ScrollReveal data-id="element-2019" data-mp="1bzakllsqlsxd">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2020" data-mp="1c14y4f1oiirf">
                  Who We Partner With
                </h2>
                <p className="text-xl text-gray-400 leading-relaxed mb-8" data-id="element-2021" data-mp="jawiouyj2cgj">
                  We don't work with everyone. Our systems are built
                  specifically for operators who are serious about capturing
                  demand and closing jobs.
                </p>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-7" data-id="element-2022" data-mp="29kzmphbscatd">
              <div className="space-y-6" data-id="element-2023" data-mp="24npwhzgknv4h">
                <ScrollReveal delay={0.1} data-id="element-2024" data-mp="24mocv6lhq0b5">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2025" data-mp="e6ca9ipo4l3d">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2026" data-mp="j4xa3h10d2ra">
                      <BuildingIcon className="w-5 h-5 text-viridia" data-id="element-2027" data-mp="n5zf6ehlw916" />
                    </div>
                    <div data-id="element-2028" data-mp="s76ia08t0k1i">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2029" data-mp="s7exhyseg1fv">
                        Local Home Service Businesses
                      </h3>
                      <p className="text-gray-400" data-id="element-2030" data-mp="123rt527i8pd9">
                        HVAC, plumbing, landscaping, pest control, painting,
                        roofing, window installers, and general contractors.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.2} data-id="element-2031" data-mp="1fwhfj7qy3waw">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2032" data-mp="1ftlg1ezg8zkc">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2033" data-mp="1uyptxdzkrxrv">
                      <TargetIcon className="w-5 h-5 text-viridia" data-id="element-2034" data-mp="1pz3eqz06s7gf" />
                    </div>
                    <div data-id="element-2035" data-mp="1l00cfmblcfc5">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2036" data-mp="1l1bdhednj8zk">
                        Companies Investing in Growth
                      </h3>
                      <p className="text-gray-400" data-id="element-2037" data-mp="2dp7hz59gq56w">
                        Businesses spending meaningfully on ads or actively
                        trying to increase their booked jobs.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.3} data-id="element-2038" data-mp="1zxspm7to0rn6">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2039" data-mp="1zwrfq6odyb71">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2040" data-mp="2wh9r7vjryj74">
                      <UsersIcon className="w-5 h-5 text-viridia" data-id="element-2041" data-mp="2rn5ltgq5qj85" />
                    </div>
                    <div data-id="element-2042" data-mp="ptj6bzjq2fwo">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2043" data-mp="uq9m0lrm79hk">
                        Operators Seeking Better Systems
                      </h3>
                      <p className="text-gray-400" data-id="element-2044" data-mp="g1d6k3n8au6x">
                        Owners who know they need better follow-up, faster
                        response times, and organized lead handling.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / SYSTEM */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-2045" data-mp="3ta9g8pifdd3n">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2046" data-mp="22dq3j9vdllu">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2047" data-mp="1rgk169gxxz53">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2048" data-mp="1rez52feza854">
              The Speed-to-Lead System
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2049" data-mp="3gxe7u6woh919">
              How we turn an inbound inquiry into a booked appointment before
              your competitor even checks their phone.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative" data-id="element-2050" data-mp="3qos8wj4a2gwi">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gray-800 z-0" data-id="element-2051" data-mp="3lvphohmz3jjh"></div>

            {[{
            step: '1',
            title: 'Lead Comes In',
            desc: 'A prospect finds you via targeted ads or local search and sends a message, form, or calls.'
          }, {
            step: '2',
            title: 'Instant AI Reply',
            desc: 'Within seconds, our AI assistant responds, preventing the lead from moving to the next company.'
          }, {
            step: '3',
            title: 'Qualification',
            desc: 'The AI asks specific questions to qualify the prospect based on your actual service offerings.'
          }, {
            step: '4',
            title: 'Follow-Up',
            desc: 'If they do not book immediately, automated nurture sequences keep your business top-of-mind.'
          }, {
            step: '5',
            title: 'Job Booked',
            desc: 'The qualified estimate is scheduled directly on your calendar. You just show up and close.'
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-2052" data-mp="1ff3nahyqwupo" data-mpi={index}>
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full" data-id="element-2053" data-mp="1kcn3hnf07hk9">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4 text-viridia font-bold" data-id="element-2054" data-mp="2quij3if7xgj7">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-2055" data-mp="k8cjsmji5luh">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-2056" data-mp="ag6slf12r8gc">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SERVICE HIERARCHY */}
      <section className="py-24 bg-viridia-black" data-id="element-2057" data-mp="gg0tl3wf7jy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2058" data-mp="3sla1zby2q6mk">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2059" data-mp="3nn8gylh37x8g">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2060" data-mp="3nns20b21b1qp">
              AI Receptionist, Ad Management & CRM Automation
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2061" data-mp="1lwf07uvd59yv">
              The systems we build for home service businesses to capture
              demand, respond instantly, and convert it into booked jobs.
            </p>
          </ScrollReveal>

          <div className="space-y-6" data-id="element-2062" data-mp="28qbvp3z6d4p7">
            {/* Primary Service */}
            <ScrollReveal data-id="element-2063" data-mp="23rihf9c2d9nz">
              <Link to="/ai-lead-conversion" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 hover:border-viridia/50 transition-colors" data-id="element-2064" data-mp="23sac8hxu73d7">
                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center" data-id="element-2065" data-mp="1tzw7rhfdjinc">
                  <div className="w-16 h-16 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-2066" data-mp="1pyaonec8y84p">
                    <ZapIcon className="w-8 h-8 text-viridia" data-id="element-2067" data-mp="1g27o2cx1mhjv" />
                  </div>
                  <div className="flex-grow" data-id="element-2068" data-mp="1g4aeuz60sepn">
                    <div className="text-xs text-viridia font-bold tracking-wide uppercase mb-1" data-id="element-2069" data-mp="1b3wgk8u80j4t">
                      Flagship System
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-viridia transition-colors" data-id="element-2070" data-mp="3adupwjecri9r">
                      AI Lead Conversion & Automations
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-2071" data-mp="3dwbqfkhwfe32">
                      Never miss a lead. We implement AI receptionists, instant
                      text-back, and automated follow-up sequences so every
                      inquiry is handled immediately.
                    </p>
                  </div>
                  <div className="hidden md:flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-full bg-gray-800 group-hover:bg-viridia transition-colors" data-id="element-2072" data-mp="2uzk38zz8ketq">
                    <ArrowRightIcon className="w-5 h-5 text-white" data-id="element-2073" data-mp="2z2p28sk3xydm" />
                  </div>
                </div>
              </Link>
            </ScrollReveal>

            {/* Secondary Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2074" data-mp="38wp4mmo08pin">
              <ScrollReveal delay={0.1} data-id="element-2075" data-mp="j97x3deu1dkp">
                <Link to="/ad-campaigns" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-2076" data-mp="o8uelz1p1fzb">
                  <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center mb-6" data-id="element-2077" data-mp="3prrllfog7hpf">
                    <TargetIcon className="w-6 h-6 text-red-500" data-id="element-2078" data-mp="8htm13l85z3e" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-2079" data-mp="3jibsrr0pmkn">
                    Ad Campaigns & CPC
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2080" data-mp="370qpr0imk2a3">
                    As a Yelp Advertising Premier Partner, we manage targeted ad
                    spend to drive high-intent local traffic directly into your
                    conversion system.
                  </p>
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2} data-id="element-2081" data-mp="2t7sq31z5fo0q">
                <Link to="/web-design-seo" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-2082" data-mp="2t6r1dup7n30j">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-2083" data-mp="18j6rkbknto60">
                    <GlobeIcon className="w-6 h-6 text-blue-500" data-id="element-2084" data-mp="tuk23h1bjg12" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-2085" data-mp="ysc6haj5gum5">
                    Conversion Websites
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2086" data-mp="1rhj1w0nb5436">
                    We build fast, mobile-first websites positioned as
                    conversion assets, not just digital brochures. Designed to
                    turn visitors into booked appointments.
                  </p>
                </Link>
              </ScrollReveal>
            </div>

            {/* Supporting Services */}
            <ScrollReveal delay={0.3} data-id="element-2087" data-mp="647y2h9723fr">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-2088" data-mp="6596edytsbts">
                <h3 className="text-lg font-bold text-white mb-6" data-id="element-2089" data-mp="mbmajl45kb2l">
                  Supporting Infrastructure
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2090" data-mp="hjb6q76ak54r">
                  <Link to="/crm-business-tools" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2091" data-mp="cmkrtn683fkx">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2092" data-mp="15aqam4dpuui4">
                      <BarChart3Icon className="w-5 h-5 text-gray-400" data-id="element-2093" data-mp="157ucog1vc5to" />
                      <h4 className="font-bold text-white" data-id="element-2094" data-mp="10bwyafa619jj">CRM Tools</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2095" data-mp="va7m2wb7iptv">
                      Centralized pipeline visibility and organization.
                    </p>
                  </Link>
                  <Link to="/reputation-management" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2096" data-mp="3g31bcofyy3lo">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2097" data-mp="1rjf26p7otqb9">
                      <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-2098" data-mp="1vk7b1hcndt70" />
                      <h4 className="font-bold text-white" data-id="element-2099" data-mp="1vi4lszjbk43c">Reputation</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2100" data-mp="20h7gpr5l4txl">
                      Automated review requests to build local trust.
                    </p>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2101" data-mp="2a8by0xiqgqvl">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2102" data-mp="2j9jt76pydblg">
                      <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-2103" data-mp="2o8wc5gdpdyir" />
                      <h4 className="font-bold text-white" data-id="element-2104" data-mp="2o83ed4wq2jcr">SEO / GEO</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2105" data-mp="oxli0z8325mx">
                      Local search optimization for discoverability.
                    </p>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* COMPARISON: AGENCIES VS VIRIDIA */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-2106" data-mp="266fr1y436jyx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2107" data-mp="264cx52ztc6ox">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2108" data-mp="216l4g6rcggjo">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2109" data-mp="2g4dh8r5iaqu6">
              What Most Agencies Sell vs.
              <br data-id="element-2110" data-mp="2b6byy8fwmvjr" />
              What We Actually Fix
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2111" data-mp="2owxje1l0bdjr">
              We don't just send you clicks and hope for the best. We fix the
              operational gaps where revenue leaks out.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2112" data-mp="2yulckn2ysczt">
            {/* The Old Way */}
            <ScrollReveal data-id="element-2113" data-mp="2twjua4dd4hpe">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-2114" data-mp="2txcsui2640wg">
                <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-2115" data-mp="3dj4y66k84obv">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-2116" data-mp="3ifvb4l0qf3t2">
                    <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-2117" data-mp="33iulcn23pix8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-2118" data-mp="38hpae6qnw3ps">
                    Most Marketing Agencies
                  </h3>
                </div>
                <ul className="space-y-6" data-id="element-2119" data-mp="2zi29rn5rb1xs">
                  <li className="flex items-start gap-4" data-id="element-2120" data-mp="2zj3w4ns83aut">
                    <PhoneOffIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2121" data-mp="2kt62hsbixsy8" />
                    <div data-id="element-2122" data-mp="2ps8zqql9iuvn">
                      <h4 className="font-bold text-white mb-1" data-id="element-2123" data-mp="2prppzx5rncsp">
                        Ignore Missed Calls
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2124" data-mp="2bszuun2ibn6z">
                        They run ads, but if you're on a jobsite and miss the
                        call, that ad spend is wasted.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2125" data-mp="2ra7wram54ji3">
                    <TrendingDownIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2126" data-mp="2r8n3dobkb6lt" />
                    <div data-id="element-2127" data-mp="2mdhfijylnhv3">
                      <h4 className="font-bold text-white mb-1" data-id="element-2128" data-mp="2hcjzoe12azwr">
                        Send Low-Intent Traffic
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2129" data-mp="2dbi01w38glu9">
                        They focus on cheap clicks that clog your pipeline with
                        tire-kickers instead of serious buyers.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2130" data-mp="1ymljtbr0w2ik">
                    <LockIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2131" data-mp="1yjzd1ebyhr1e" />
                    <div data-id="element-2132" data-mp="1totqqeen6acs">
                      <h4 className="font-bold text-white mb-1" data-id="element-2133" data-mp="1pldwtptyio3s">
                        Long-Term Contracts
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2134" data-mp="521tn39gx188">
                        They lock you into 6 or 12 month retainers with zero
                        visibility into actual performance.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            {/* The Viridia Way */}
            <ScrollReveal delay={0.2} data-id="element-2135" data-mp="3hl09jbsl1y9p">
              <div className="bg-viridia-black border-2 border-viridia/50 rounded-2xl p-8 h-full relative overflow-hidden" data-id="element-2136" data-mp="3hm2y3d2fnomg">
                <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-2137" data-mp="3mhi79rphwwcj"></div>
                <div className="relative z-10" data-id="element-2138" data-mp="3rguvafs3s9gp">
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-2139" data-mp="51u7b74iwq3">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-2140" data-mp="2ysz39odhb5q9">
                      <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-2141" data-mp="33rsgrgsrmwqf" />
                    </div>
                    <h3 className="text-2xl font-bold text-white" data-id="element-2142" data-mp="2dkhguqbo28sa">
                      Viridia Analytics
                    </h3>
                  </div>
                  <ul className="space-y-6" data-id="element-2143" data-mp="24ihvjltmcijj">
                    <li className="flex items-start gap-4" data-id="element-2144" data-mp="24hq2ahnhuwwf">
                      <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2145" data-mp="28jti1ih74nni" />
                      <div data-id="element-2146" data-mp="1usonuvwlknkk">
                        <h4 className="font-bold text-white mb-1" data-id="element-2147" data-mp="1uset6v0lxs8j">
                          Instant AI Follow-Up
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2148" data-mp="1zlhrthk8rrvl">
                          Our AI replies to messages and missed calls in
                          seconds, booking estimates 24/7 so leads stop
                          slipping.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-2149" data-mp="1gsxcz41dnnas">
                      <BarChart3Icon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2150" data-mp="1gsdw7qngr4yc" />
                      <div data-id="element-2151" data-mp="x7wxsau5g5e9">
                        <h4 className="font-bold text-white mb-1" data-id="element-2152" data-mp="x980e7buz73s">
                          Organized Pipeline
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2153" data-mp="16822uebverp9">
                          We set up a CRM that shows every lead, every job, and
                          every dollar cleanly, replacing sticky notes and
                          chaos.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-2154" data-mp="1b6dcop26127h">
                      <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2155" data-mp="1pwatul1ogrn6" />
                      <div data-id="element-2156" data-mp="1kwya4963s0ot">
                        <h4 className="font-bold text-white mb-1" data-id="element-2157" data-mp="1kxr6qhbcl9t8">
                          Month-to-Month Partnership
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2158" data-mp="1yu4lhiohyx40">
                          Flexible packages with real reporting. We sit between
                          you and the ad platforms to ensure transparency.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="py-24 bg-viridia-black border-b border-gray-800" data-id="element-2159" data-mp="15cxwchrc8tks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2160" data-mp="1ab99b1cxjk78">
          <ScrollReveal data-id="element-2161" data-mp="1f9kfn240nckp">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-2162" data-mp="1f8rjev2oo5gt">
              <div className="max-w-2xl" data-id="element-2163" data-mp="1k8453chtj6ht">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2164" data-mp="1k95fder0foyh">
                  Documented Results
                </h2>
                <p className="text-lg text-gray-400" data-id="element-2165" data-mp="3m00g8l02qec2">
                  Real numbers from real local service businesses.
                </p>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 inline-flex items-center text-white hover:text-gray-300 font-semibold transition-colors" data-id="element-2166" data-mp="3q241208muva0">
                View Proof Library
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2167" data-mp="38s9nm41oozun" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2168" data-mp="34r7um4yd1k14">
            <ScrollReveal delay={0.1} data-id="element-2169" data-mp="34ov6tb5x8fg5">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-2170" data-mp="5bt5963ochvh">
                <div data-id="element-2171" data-mp="czmoz9j60og">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-2172" data-mp="adklf5esddm">
                    Landscaping
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2173" data-mp="2h1dlnyh5wfm5">
                    TFP Landscaping
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2174" data-mp="2c0fskqum615v">
                    <strong data-id="element-2175" data-mp="3t2kfxffxztb">Challenge:</strong> 4+ hour response time meant ad
                    spend was being wasted because leads went cold before the
                    owner could reply.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2176" data-mp="3qz20gfo6ey8n">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2177" data-mp="3uzcd2b6h4mwh">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2178" data-mp="mk23yj462hf7">284%</p>
                      <p className="text-xs text-gray-400" data-id="element-2179" data-mp="mhps8np053al">More Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2180" data-mp="rgss7tq4kjbp">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2181" data-mp="dl8ct89luj91">242</p>
                      <p className="text-xs text-gray-400" data-id="element-2182" data-mp="ignmrp4hrv06">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2183" data-mp="2y7rztyaaop2b">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2184" data-mp="3397khp8k8tk6">28</p>
                      <p className="text-xs text-gray-400" data-id="element-2185" data-mp="2uqnulxpv55e5">AI Bookings</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2186" data-mp="2qqlq7gsqtxv6">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2187" data-mp="34q4ufyifop27">39.9%</p>
                      <p className="text-xs text-gray-400" data-id="element-2188" data-mp="2zqih7p2lv8te">Close Rate</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-2189" data-mp="2c1pkvri2myy9">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2190" data-mp="2h00ty00jl5ff" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2191" data-mp="1t9oem42yw6et">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-2192" data-mp="1t8lpa0lamc10">
                <div data-id="element-2193" data-mp="3odmfjj7fm2gl">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-2194" data-mp="3oe4kr5c2nr0n">
                    Painting
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2195" data-mp="1ytk4ofe3qgk">
                    Finish Coat Painting
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2196" data-mp="35eqo2fmyk26g">
                    <strong data-id="element-2197" data-mp="35gla5x9yu0c4">Challenge:</strong> Generating strong inbound demand
                    but couldn't keep up with conversations. Leads were slipping
                    through the cracks.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2198" data-mp="3jei5kqcoo0p7">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2199" data-mp="3jg2y6afft9kf">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2200" data-mp="too9um75ti5r">29</p>
                      <p className="text-xs text-gray-400" data-id="element-2201" data-mp="yn9ces9vwdxd">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2202" data-mp="18u28nudh3xc4">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2203" data-mp="13vh4jjv3otie">
                        $15.05k
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-2204" data-mp="v1ckpkedwicd">Closed-Won</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2205" data-mp="q29dpueug21r">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2206" data-mp="l2wt7gbg372c">138</p>
                      <p className="text-xs text-gray-400" data-id="element-2207" data-mp="l4zn4bfpxkcc">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2208" data-mp="g5dao47ps84l">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2209" data-mp="c4bddsxcy659">64%</p>
                      <p className="text-xs text-gray-400" data-id="element-2210" data-mp="2ags2qro76jni">Lead Growth</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-2211" data-mp="2pozllm57rqxv">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2212" data-mp="2fyo9hcbdmbfw" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2213" data-mp="25zfjeujmtgyt">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2214" data-mp="2b0moglue421s">
          <ScrollReveal data-id="element-2215" data-mp="1x0tzow2fbvwg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2216" data-mp="1x1n25kucnz8s">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2217" data-mp="3rdtlx81b7ocp">
          <ScrollReveal delay={0.2} data-id="element-2218" data-mp="7s7qqm1semu">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2219" data-mp="6qzetwf268t">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2220" data-mp="a9mhzpw75qb2" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2221" data-mp="ec9c5xgcbnbf">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2222" data-mp="t1p0l6sefgny">
          <ScrollReveal data-id="element-2223" data-mp="o33zl55bosyg">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-2224" data-mp="o3wyxl1ycg6k">
              See where your leads are leaking
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2225" data-mp="1223nuboxb5u3">
              Book a real strategy call. We'll look at your ads, website, and
              follow-up process to find exactly where you are losing jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-200 transition-all duration-300" data-id="element-2226" data-mp="2zt6c8u5h34x1">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-2227" data-mp="1a4yxekldnfx5" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}